import React, { useState, useEffect } from 'react';
import { Search, Filter, Calendar, MapPin, Phone, Mail, Clock, Eye, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, X, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { authToken } from '../globals/Global';

const BookingManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // New filter states
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [serviceFilter, setServiceFilter] = useState('All');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('All');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');
  const [cityFilter, setCityFilter] = useState('All');
  const [timePeriodFilter, setTimePeriodFilter] = useState('All');

  // Sorting states
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = authToken || localStorage.getItem('sessionToken');

      const response = await axios.get('http://stg.panditjee.com:8080/api/orders', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.status === 'SUCCESS' && response.data.data) {
        setBookings(response.data.data);
        setFilteredBookings(response.data.data);
      } else {
        throw new Error(response.data.errorMessage || 'Failed to fetch bookings');
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    let filtered = bookings;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(booking => 
        booking.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.id.toString().includes(searchTerm) ||
        booking.poojaSubCategoryName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(booking => booking.orderStatus === statusFilter);
    }

    // Filter by time period
    if (timePeriodFilter !== 'All') {
      const now = new Date();
      let startDate;
      
      switch (timePeriodFilter) {
        case '24hours':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '1week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '6weeks':
          startDate = new Date(now.getTime() - 6 * 7 * 24 * 60 * 60 * 1000);
          break;
        case '18weeks':
          startDate = new Date(now.getTime() - 18 * 7 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(booking => {
          const bookingDate = new Date(booking.createdAt);
          return bookingDate >= startDate;
        });
      }
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter(booking => {
        const bookingDate = parseDate(booking.selectedDate);
        const fromDate = new Date(dateFrom);
        return bookingDate >= fromDate;
      });
    }
    if (dateTo) {
      filtered = filtered.filter(booking => {
        const bookingDate = parseDate(booking.selectedDate);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire end date
        return bookingDate <= toDate;
      });
    }

    // Filter by service/pooja category
    if (serviceFilter !== 'All') {
      filtered = filtered.filter(booking => 
        booking.poojaSubCategoryName === serviceFilter || 
        booking.poojaCategoryName === serviceFilter
      );
    }

    // Filter by payment type
    if (paymentTypeFilter !== 'All') {
      filtered = filtered.filter(booking => booking.paymentType === paymentTypeFilter);
    }

    // Filter by payment status
    if (paymentStatusFilter !== 'All') {
      filtered = filtered.filter(booking => booking.paymentStatus === paymentStatusFilter);
    }

    // Filter by city
    if (cityFilter !== 'All') {
      filtered = filtered.filter(booking => booking.city === cityFilter);
    }

    setFilteredBookings(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, statusFilter, timePeriodFilter, dateFrom, dateTo, serviceFilter, paymentTypeFilter, paymentStatusFilter, cityFilter, bookings]);

  const parseDate = (dateString) => {
    try {
      if (!dateString) return new Date(0);

      // Format: "2025-05-27 00:00:00.000" or ISO "2025-05-27T..."
      if (dateString.length > 10) {
        return new Date(dateString);
      }

      // Format: "27-05-2025" or "27/05/2025"
      const separator = dateString.includes('-') ? '-' : '/';
      const parts = dateString.split(separator);

      // Check if it's dd-mm-yyyy or yyyy-mm-dd
      const [first, month, last] = parts;
      const day = first.length === 4 ? last : first;
      const year = first.length === 4 ? first : last;

      return new Date(year, month - 1, day);
    } catch {
      return new Date(0);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedBookings = () => {
    if (!sortColumn) return filteredBookings;

    const sorted = [...filteredBookings].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'customer':
          aValue = a.userName.toLowerCase();
          bValue = b.userName.toLowerCase();
          break;
        case 'service':
          aValue = (a.poojaSubCategoryName || '').toLowerCase();
          bValue = (b.poojaSubCategoryName || '').toLowerCase();
          break;
        case 'date':
          aValue = parseDate(a.selectedDate).getTime();
          bValue = parseDate(b.selectedDate).getTime();
          break;
        case 'amount':
          aValue = parseFloat(a.totalAmount) || 0;
          bValue = parseFloat(b.totalAmount) || 0;
          break;
        case 'status':
          aValue = (a.orderStatus || '').toLowerCase();
          bValue = (b.orderStatus || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4" /> : 
      <ArrowDown className="w-4 h-4" />;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';

      // Format: "2025-05-27 00:00:00.000" or ISO "2025-05-27T..."
      if (dateString.length > 10) {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      // Format: "27-05-2025" or "27/05/2025"
      const separator = dateString.includes('-') ? '-' : '/';
      const parts = dateString.split(separator);

      // Check if it's dd-mm-yyyy or yyyy-mm-dd
      const [first, month, last] = parts;
      const day = first.length === 4 ? last : first;
      const year = first.length === 4 ? first : last;

      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setDateFrom('');
    setDateTo('');
    setServiceFilter('All');
    setPaymentTypeFilter('All');
    setPaymentStatusFilter('All');
    setCityFilter('All');
    setTimePeriodFilter('All');
    setSortColumn(null);
    setSortDirection('asc');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (statusFilter !== 'All') count++;
    if (timePeriodFilter !== 'All') count++;
    if (dateFrom || dateTo) count++;
    if (serviceFilter !== 'All') count++;
    if (paymentTypeFilter !== 'All') count++;
    if (paymentStatusFilter !== 'All') count++;
    if (cityFilter !== 'All') count++;
    return count;
  };

  // Pagination logic
  const sortedBookings = getSortedBookings();
  const totalPages = Math.ceil(sortedBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = sortedBookings.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Get unique values for filters
  const uniqueStatuses = ['All', ...new Set(bookings.map(b => b.orderStatus).filter(Boolean))];
  const uniqueServices = ['All', ...new Set(bookings.map(b => b.poojaSubCategoryName || b.poojaCategoryName).filter(Boolean))];
  const uniquePaymentTypes = ['All', ...new Set(bookings.map(b => b.paymentType).filter(Boolean))];
  const uniquePaymentStatuses = ['All', ...new Set(bookings.map(b => b.paymentStatus).filter(Boolean))];
  const uniqueCities = ['All', ...new Set(bookings.map(b => b.city).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-orange-100">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Booking Management</h1>
          <p className="text-gray-600">Manage and track all pooja bookings</p>
        </div>

        {/* Search and Quick Filters */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-orange-100">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Search */}
              <div className="relative lg:col-span-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, or booking ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
                >
                  <option value="All">All Statuses</option>
                  {uniqueStatuses.filter(s => s !== 'All').map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center bg-orange-50 rounded-lg p-3">
                <span className="text-sm text-gray-600">Total: </span>
                <span className="ml-2 text-lg font-semibold text-orange-600">{filteredBookings.length}</span>
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="flex items-center justify-between border-t pt-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              >
                <Filter className="w-5 h-5" />
                <span className="font-medium">Advanced Filters</span>
                {getActiveFilterCount() > 0 && (
                  <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {getActiveFilterCount()}
                  </span>
                )}
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {getActiveFilterCount() > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear All Filters
                </button>
              )}
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Time Period Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Time Period
                    </label>
                    <select
                      value={timePeriodFilter}
                      onChange={(e) => setTimePeriodFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white text-sm"
                    >
                      <option value="All">All Time</option>
                      <option value="24hours">Last 24 Hours</option>
                      <option value="1week">Last 1 Week</option>
                      <option value="6weeks">Last 6 Weeks</option>
                      <option value="18weeks">Last 18 Weeks</option>
                    </select>
                  </div>

                  {/* Date Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Date Range
                    </label>
                    <div className="space-y-2">
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        placeholder="From"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      />
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        placeholder="To"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Service Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service/Pooja
                    </label>
                    <select
                      value={serviceFilter}
                      onChange={(e) => setServiceFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white text-sm"
                    >
                      {uniqueServices.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  {/* Payment Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Type
                    </label>
                    <select
                      value={paymentTypeFilter}
                      onChange={(e) => setPaymentTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white text-sm"
                    >
                      {uniquePaymentTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Payment Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Status
                    </label>
                    <select
                      value={paymentStatusFilter}
                      onChange={(e) => setPaymentStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white text-sm"
                    >
                      {uniquePaymentStatuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  {/* City Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      City
                    </label>
                    <select
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white text-sm"
                    >
                      {uniqueCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading bookings...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-red-200">
            <div className="text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading Bookings</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchBookings}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Bookings Table */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                  <tr>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-600 transition-colors"
                      onClick={() => handleSort('id')}
                    >
                      <div className="flex items-center gap-2">
                        Booking ID
                        {getSortIcon('id')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-600 transition-colors"
                      onClick={() => handleSort('customer')}
                    >
                      <div className="flex items-center gap-2">
                        Customer
                        {getSortIcon('customer')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-600 transition-colors"
                      onClick={() => handleSort('service')}
                    >
                      <div className="flex items-center gap-2">
                        Service
                        {getSortIcon('service')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-600 transition-colors"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center gap-2">
                        Selected Date
                        {getSortIcon('date')}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Address</th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-600 transition-colors"
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center gap-2">
                        Amount
                        {getSortIcon('amount')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-orange-600 transition-colors"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-2">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBookings.map((booking, index) => (
                    <tr key={booking.id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-orange-50 transition-colors`}>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">#{booking.id}</div>
                      </td>
                      <td className="px-2 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{booking.userName}</div>
                          <div className="text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {booking.userPhoneNo}
                          </div>
                          <div className="text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[150px]">{booking.userEmail}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{booking.poojaSubCategoryName || 'Pooja Service'}</div>
                          <div className="text-gray-500">Qty: {booking.quantity}</div>
                          {booking.purohitName && (
                            <div className="text-gray-500">Purohit: {booking.purohitName}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 flex items-center gap-1">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            {formatDate(booking.selectedDate)}
                          </div>
                          <div className="text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            {booking.selectedTime || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate max-w-[120px]">{booking.addressLine1}</span>
                          </div>
                          <div className="text-gray-500">{booking.city}, {booking.state}</div>
                          <div className="text-gray-500">{booking.zipcode}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-semibold text-green-600 flex items-center gap-1">
                            ₹{booking.totalAmount}
                          </div>
                          <div className="text-gray-500">({booking.paymentType})</div>
                        </div>
                      </td>
                      <td className="px-2 py-4">
                        <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.orderStatus)}`}>
                          {getStatusIcon(booking.orderStatus)}
                          {booking.orderStatus}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setShowModal(true);
                            }}
                            className="flex items-center gap-1 px-3 py-1 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredBookings.length > 0 && (
              <div className="border-t border-gray-200 bg-white px-6 py-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      entries (Showing {startIndex + 1} to {Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length})
                    </span>
                  </div>

                  {/* Page navigation */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="flex gap-1">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-orange-500 text-white font-semibold'
                                : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Results */}
        {!loading && !error && filteredBookings.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12">
            <div className="text-center">
              <div className="text-gray-400 text-lg mb-2">No bookings found</div>
              <div className="text-gray-500 text-sm mb-4">Try adjusting your search or filter criteria</div>
              {getActiveFilterCount() > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && selectedBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Booking Details #{selectedBooking.id}</h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-white hover:text-gray-200 text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Service Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Service Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Service:</span>
                      <div className="font-medium">{selectedBooking.poojaSubCategoryName || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Category:</span>
                      <div className="font-medium">{selectedBooking.poojaCategoryName || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Date & Time:</span>
                      <div className="font-medium">{formatDate(selectedBooking.selectedDate)} at {selectedBooking.selectedTime}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Quantity:</span>
                      <div className="font-medium">{selectedBooking.quantity}</div>
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Customer Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <div className="font-medium">{selectedBooking.userName}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Phone:</span>
                      <div className="font-medium">{selectedBooking.userPhoneNo}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-600">Email:</span>
                      <div className="font-medium">{selectedBooking.userEmail}</div>
                    </div>
                  </div>
                </div>

                {/* Address Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Address Details</h3>
                  <div className="text-sm">
                    <div className="mb-2">
                      <span className="text-gray-600">Type:</span>
                      <span className="ml-2 font-medium">{selectedBooking.addressType}</span>
                    </div>
                    <div className="font-medium">
                      {selectedBooking.addressLine1}
                      {selectedBooking.addressLine2 && <>, {selectedBooking.addressLine2}</>}
                      <br />
                      {selectedBooking.city}, {selectedBooking.state} {selectedBooking.zipcode}
                      <br />
                      {selectedBooking.country}
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Payment Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Amount:</span>
                      <div className="font-medium text-green-600">₹{selectedBooking.totalAmount}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Payment Type:</span>
                      <div className="font-medium">{selectedBooking.paymentType}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Payment Status:</span>
                      <div className={`font-medium inline-flex items-center gap-1 px-2 py-1 rounded ${getStatusColor(selectedBooking.paymentStatus)}`}>
                        {getStatusIcon(selectedBooking.paymentStatus)}
                        {selectedBooking.paymentStatus}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Order Status:</span>
                      <div className={`font-medium inline-flex items-center gap-1 px-2 py-1 rounded ${getStatusColor(selectedBooking.orderStatus)}`}>
                        {getStatusIcon(selectedBooking.orderStatus)}
                        {selectedBooking.orderStatus}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Timeline</h3>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-gray-600">Created:</span>
                      <span className="ml-2 font-medium">{new Date(selectedBooking.createdAt).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Last Updated:</span>
                      <span className="ml-2 font-medium">{new Date(selectedBooking.updatedAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingManagement;